import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { IMAGE_MAX_SIZES } from '@epicuro-next/ataraxia/image';
import {
  MediaObjectFit,
  SpritesPaths,
  SPRITES_PATHS_TOKEN,
  UTCDeliveryPeriod,
} from '@epicuro-next/ataraxia/models';
import {
  defaultFilterText,
  headerText,
  headerTooltipText,
} from '@epicuro-next/constants/features/localization';
import { ITopArticle } from '@epicuro-next/data/reporting-api';
import { Media, MediaType } from '@epicuro-next/models/engagement';
import { selectActiveAccountBrandOrg } from '@epicuro-next/state/accounts-state';
import { selectBestPerformingDashboard } from '@epicuro-next/state/analytics-entities';
import { selectFilterIconsParsed } from '@epicuro-next/state/p-basket-state';
import { selectBestListMinimumMonthsLimitCalendarMinDate } from '@epicuro-next/state/product-list-state';
import { openOverlayOutlet } from '@epicuro-next/state/router-store';
import { formatUTCRange, Placement } from '@epicuro-next/utilities';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { DateTime } from 'luxon';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { map, withLatestFrom } from 'rxjs/operators';

import { DateFilterOptionLabel, dateFilterOptions } from '../../data';
import { BestPerformingArticleQuery } from '../../models';
import { DashboardColumnSort } from '../../models/dashboard-column-sort';
import { BestPerformingArticlesStore } from '../../store';
import { deductDaysFilter } from '../../utils';

@Component({
  selector: 'epc-best-performing-articles',
  templateUrl: './best-performing-articles.component.html',
  styleUrls: ['./best-performing-articles.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [BestPerformingArticlesStore],
  standalone: false,
})
export class BestPerformingArticlesComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  private readonly store = inject(Store);
  private readonly componentStore = inject(BestPerformingArticlesStore);
  public readonly spritesPaths: SpritesPaths = inject(SPRITES_PATHS_TOKEN);

  public readonly bestPerformingArticleDashboardSettings$ = this.store
    .select(selectBestPerformingDashboard)
    .pipe(map((settings) => settings.dashboards?.articles));

  private preselectBrandOrgFromAccountByDefault$ =
    this.bestPerformingArticleDashboardSettings$.pipe(
      map(
        (bestPerformingArticleDashboardSettings) =>
          bestPerformingArticleDashboardSettings?.brandOrgFilter?.enabled &&
          bestPerformingArticleDashboardSettings?.brandOrgFilter
            ?.selectFromAccountByDefault,
      ),
    );

  public shouldMediaPlay$$ = new BehaviorSubject(false);

  public configStripRedirection$ =
    this.bestPerformingArticleDashboardSettings$.pipe(
      map((dashBoardSettings) => dashBoardSettings?.configStripRedirection),
    );

  public preselectedBrandOrg$ = this.store
    .select(selectActiveAccountBrandOrg)
    .pipe(
      withLatestFrom(this.preselectBrandOrgFromAccountByDefault$),
      map(([activeAccountBrandOrg, preselectBrandOrgFromAccountByDefault]) => {
        if (!preselectBrandOrgFromAccountByDefault) {
          return;
        }

        return activeAccountBrandOrg;
      }),
    );

  public preselectedDateRangeFilter$ =
    this.bestPerformingArticleDashboardSettings$.pipe(
      map((bestPerformingArticleDashboardSettings$) => {
        const valueFromConfig =
          bestPerformingArticleDashboardSettings$?.preselectedDateRangeFilterValue?.toLowerCase();
        const dateFilterOptionFound = dateFilterOptions.find(
          (dfo) => dfo.id.toLowerCase() === valueFromConfig,
        );
        return dateFilterOptionFound ?? dateFilterOptions;
      }),
    );

  public preselectedSort$ = this.bestPerformingArticleDashboardSettings$.pipe(
    map((settings) => settings?.sorting),
  );

  public currency$ = this.bestPerformingArticleDashboardSettings$.pipe(
    map((settings) => settings?.currency),
  );

  public readonly bestPerformingArticles$ =
    this.componentStore.bestPerformingArticles$;
  public readonly filters$ = this.componentStore.filters$;
  public readonly hasError$ = this.componentStore.hasError$;
  public readonly isLoading$ = this.componentStore.loading$;
  public readonly hasFiltersSelected$ = this.componentStore.hasFiltersSelected$;

  public readonly selectedSort$ = this.componentStore.selectedSort$;

  public readonly selectedBrandOrgs$ = this.componentStore.selectedBrandOrg$;

  public dateFilterOptions = dateFilterOptions;
  public selectedDateOption$$ = new BehaviorSubject<string>(
    dateFilterOptions[0].label,
  );
  public selectedOptionText$ = this.selectedDateOption$$.asObservable();
  public placement = Placement;
  public minDate$ = this.store.select(
    selectBestListMinimumMonthsLimitCalendarMinDate,
  );
  public maxDate = DateTime.utc().toJSDate();

  public readonly selectedOrderTypes$ = this.componentStore.selectedOrderType$;

  public readonly selectedCategories$ = this.componentStore.selectedCategories$;

  public readonly filterIcons$ = this.store.select(selectFilterIconsParsed);

  public skeletons = new Array(4);
  public imageMaxSizes = IMAGE_MAX_SIZES;
  public objectFit = MediaObjectFit;
  public baseTextHeightSize = '1rem';

  protected readonly defaultFilterText = defaultFilterText;
  protected readonly headerText = headerText;
  protected readonly headerTooltipText = headerTooltipText;

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public ngOnInit(): void {
    this.preselectedDateRangeFilter$
      .pipe(
        takeUntil(this.destroy$),
        concatLatestFrom(() => [
          this.preselectedBrandOrg$,
          this.preselectedSort$,
        ]),
      )
      .subscribe(
        ([
          preselectedDateRangeFilter,
          preselectedBrandOrg,
          preselectedSort,
        ]) => {
          const { id, label } =
            preselectedDateRangeFilter as DateFilterOptionLabel;
          const dateRange = deductDaysFilter(id);

          this.setStoreParams({
            brandOrgIds: preselectedBrandOrg ? [preselectedBrandOrg] : [],
            dateRange,
            columnSort: preselectedSort,
          });

          this.selectedDateOption$$.next(label);
        },
      );
  }

  public setStoreParams(payload: Partial<BestPerformingArticleQuery>) {
    this.componentStore.setParams(payload);
  }

  public filterListByDate({ id, label }: DateFilterOptionLabel) {
    const dateRange = deductDaysFilter(id);

    this.setStoreParams({ dateRange });
    this.selectedDateOption$$.next(label);
  }

  public getFilterIconValue(
    filterValue: string,
    filterIconsConfig: { [p: string]: { [p: string]: string } },
  ) {
    const categoryIcons = filterIconsConfig?.category;
    return categoryIcons?.[filterValue] ?? categoryIcons?.default ?? null;
  }

  public convertToMedia(imageUrl: string): Media {
    return { url: imageUrl, mediaType: MediaType.Image };
  }

  public trackByProductId(_: number, article: ITopArticle) {
    return article.productId;
  }

  public sortingClicked(columnSort: DashboardColumnSort) {
    this.setStoreParams({ columnSort });
  }

  public customRangeSelected(dateRange: Required<UTCDeliveryPeriod>) {
    this.setStoreParams({ dateRange });

    const rangeText = formatUTCRange(`${dateRange.from}/${dateRange.to}`);

    this.selectedDateOption$$.next(rangeText);
  }

  public openConfigStripPage(article: ITopArticle): void {
    const overlayPath = [
      'cs',
      article.brandId,
      article.parentProductId ?? article.productId,
      article.productId,
    ];

    this.store.dispatch(
      openOverlayOutlet({
        overlayPath,
      }),
    );
  }
}
